import React, { useState } from 'react';
import { Container, Row, Col, Button, Form, Alert } from 'react-bootstrap';
import { Envelope } from 'react-bootstrap-icons';
import logoHorizontal from '../assets/logos/logo-ai-black-horizontal.png';
import logoVertical from '../assets/logos/logo-ai-black-vertical.png';
import homeBlob from '../assets/home/home-blob.png';
import homeApp from '../assets/home/home-app.png';
import iconChat from '../assets/comingsoon/icon-chat.png';
import iconMap from '../assets/comingsoon/icon-map.png';
import iconTools from '../assets/comingsoon/icon-tools.png';
import iconIdeas from '../assets/comingsoon/icon-ideas.png';
import iconWorkshop from '../assets/comingsoon/icon-workshop.png';
import homeAppConversations from '../assets/home/home-app-conversations.png';
import api from '../services/api';
import './LandingPage.css';
import './ComingSoon.css';

const ComingSoon: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      await api.post('/api/waitlist', { email });
      setSubmitStatus({
        type: 'success',
        message: "You're on the list! We'll notify you when Revealer AI launches.",
      });
      setEmail('');
    } catch (error: any) {
      setSubmitStatus({
        type: 'error',
        message: error.response?.data?.error || 'Failed to join waitlist. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="landing-page">
      <section>
        <Container>
          <Row>
            <Col className="pt-5 text-start">
              <img src={logoHorizontal} alt="Revealer AI" className="logo-horizontal" />
            </Col>
          </Row>
          <Row className="align-items-center min-vh-75">
            <Col lg={6}>
              <h1 className="hero-title">
                Turn Ambiguity into Action
              </h1>
              <p className="hero-subtitle">
                Revealer AI is the UX discovery and intelligence platform that transforms raw research into early design artifacts. It's a co-creator that helps you move with clarity and speed.
              </p>
              <div className="mt-4">
                {submitStatus.type && (
                  <Alert
                    variant={submitStatus.type === 'success' ? 'success' : 'danger'}
                    dismissible
                    onClose={() => setSubmitStatus({ type: null, message: '' })}
                    className="mb-3"
                  >
                    {submitStatus.message}
                  </Alert>
                )}
                <Form onSubmit={handleSubmit} className="d-flex gap-2">
                  <Form.Control
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isSubmitting}
                    className="email-input"
                  />
                  <Button
                    variant="tertiary"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'JOINING...' : 'JOIN THE WAITLIST'} <Envelope size={16} />
                  </Button>
                </Form>
              </div>
            </Col>
            <Col lg={6} className="d-flex justify-content-end">
              <div className="hero-images-container">
                <img src={homeBlob} alt="" className="img-fluid hero-blob" />
                <img src={homeApp} alt="" className="position-absolute img-fluid hero-app" />
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="vision-section vision-section-background py-5">
        <Container>
          <Row className="mb-5">
            <Col className="text-center">
              <h2 className="section-title text-center">The Blank Page is No Longer a Barrier</h2>
              <p className="section-description text-center mx-auto">
                Every great idea starts with a question. But the path from a messy  transcript or early idea to a final design can feel overwhelming. Revealer AI gives you a shortcut, providing a clear path from feeling stuck to getting started with confidence. We don't replace your thinking—we sharpen it.
              </p>
              <img src={homeAppConversations} alt="" className="img-fluid vision-section-image" />
            </Col>
          </Row>
        </Container>
      </section>

      <section className="features-section py-5">
        <Container>
          <Row className="mb-5">
            <Col>
              <h2 className="section-title">Kickstart Your Workflow. Instantly.</h2>
              <p className="section-description">
                Stop waiting for genius to strike. Our contextual templates <strong>unlock creative momentum</strong> to start your next project with clarity.
              </p>
            </Col>
          </Row>
          <Row className="g-4 justify-content-center">
            <Col md={6} lg={4}>
              <div className="difference-card feature-card">
                <div className="feature-icon mb-3">
                  <img src={iconChat} alt="Revealer AI" className="feature-card-icon" />
                </div>
                <h4>From Chat to Wireframe.</h4>
                <p>
                  Describe your project in a simple chat and instantly generate a foundational wireframe. Then, export directly to <strong>Figma&trade;</strong> for further refinement and collaboration.
                </p>
              </div>
            </Col>
            <Col md={6} lg={4}>
              <div className="difference-card feature-card">
                <div className="feature-icon mb-3">
                  <img src={iconMap} alt="Revealer AI" className="feature-card-icon" />
                </div>
                <h4>Align Your Team with an Actionable Roadmap.</h4>
                <p>
                  Turn stakeholder notes and research findings into a clear, prioritized UX roadmap. Ensure your team is always aligned on the right next steps.
                </p>
              </div>
            </Col>
            <Col md={6} lg={4}>
              <div className="difference-card feature-card">
                <div className="feature-icon mb-3">
                  <img src={iconTools} alt="Revealer AI" className="feature-card-icon" />
                </div>
                <h4>Break Through Creative Blocks.</h4>
                <p>
                  Discover new ideas and challenge assumptions with AI-powered  ideation. Generate concepts and structured feedback to find the best solutions.
                </p>
              </div>
            </Col>
            <Col md={6} lg={4}>
              <div className="difference-card feature-card">
                <div className="feature-icon mb-3">
                  <img src={iconIdeas} alt="Revealer AI" className="feature-card-icon-large" />
                </div>
                <h4>Bring Your Users to Life.</h4>
                <p>
                  Transform research data into detailed user personas. Get a deeper understanding of your target audience's motivations and frustrations.
                </p>
              </div>
            </Col>
            <Col md={6} lg={4}>
              <div className="difference-card feature-card">
                <div className="feature-icon mb-3">
                  <img src={iconWorkshop} alt="Revealer AI" className="feature-card-icon-large" />
                </div>
                <h4>Get Research-Based Design Feedback.</h4>
                <p>
                  Stop guessing and start validating. Get actionable, research-based feedback on your designs to build with confidence.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="vision-section enterprise-security-section py-5">
        <Container>
          <Row className="mb-5">
            <Col>
              <h2 className="section-title enterprise-text-white">Enterprise-Grade Security</h2>
              <p className="section-description enterprise-text-white">
                We take your data and privacy seriously. Revealer AI uses enterprise-grade security protocols. Your research and designs are safe with us.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="connect-section py-5 text-center">
        <Container>
          <h2 className="mb-4 section-title">Don't Miss Out</h2>
          <p className="mb-4">
            Join the ones in motion.
          </p>
          {submitStatus.type && (
            <Alert
              variant={submitStatus.type === 'success' ? 'success' : 'danger'}
              dismissible
              onClose={() => setSubmitStatus({ type: null, message: '' })}
              className="mb-3 mx-auto alert-max-width"
            >
              {submitStatus.message}
            </Alert>
          )}
          <Form onSubmit={handleSubmit} className="d-flex justify-content-center gap-2">
            <Form.Control
              type="email"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isSubmitting}
              className="email-input"
            />
            <Button
              variant="primary"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'JOINING...' : 'JOIN THE WAITLIST'} <Envelope size={16} />
            </Button>
          </Form>
        </Container>
      </section>
      
      <footer className="landing-footer py-4 mt-5">
        <Container>
          <Row className="align-items-center">
            <Col>
              <div className="d-flex align-items-center gap-4">
                <img src={logoVertical} alt="Revealer AI" className="logo-vertical" />
                <span className="small text-muted">© revealer ai 2026</span>
              </div>
            </Col>
            <Col xs="auto">
              <span className="small text-muted">All Rights Reserved</span>
            </Col>
          </Row>
        </Container>
      </footer>
    </div>
  );
};

export default ComingSoon;